import apiClient from './axios.config';
import {
  TokenDto,
  LoginUserDto,
  CreateUserDto,
  RefreshTokenDto,
  RequestChangePasswordUserDto,
  ForgotPasswordDto,
  UserDto,
  ChangeLocaleDto,
  ChangeUsernameDto,
  OAuthCodeRequestDto,
  VkIdCodeRequestDto,
} from '../types';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  storePKCEData,
} from '../utils/pkce';
import { OAUTH_CONFIG } from '../constants/config';

export const authApi = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginUserDto): Promise<TokenDto> => {
    const response = await apiClient.post<TokenDto>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Register new user
   */
  register: async (userData: CreateUserDto): Promise<UserDto> => {
    const response = await apiClient.post<UserDto>('/auth/register', userData);
    return response.data;
  },

  /**
   * Request password reset email
   */
  requestPasswordReset: async (data: RequestChangePasswordUserDto): Promise<void> => {
    await apiClient.post('/passwords/request', data);
  },

  /**
   * Reset password with token
   */
  resetPassword: async (data: ForgotPasswordDto): Promise<void> => {
    await apiClient.patch('/passwords/recovery', data);
  },

  /**
   * Refresh access token
   */
  refreshToken: async (refreshToken: RefreshTokenDto): Promise<TokenDto> => {
    const response = await apiClient.post<TokenDto>('/auth/token/refresh', refreshToken);
    return response.data;
  },

  /**
   * Get current user
   */
  getCurrentUser: async (): Promise<UserDto> => {
    const response = await apiClient.get<UserDto>('/users');
    return response.data;
  },

  /**
   * Resend verification email
   */
  resendVerificationEmail: async (): Promise<void> => {
    await apiClient.post('/emails/resend');
  },

  /**
   * Change user locale
   */
  changeLocale: async (data: ChangeLocaleDto): Promise<void> => {
    await apiClient.patch('/users/locale', data);
  },

  /**
   * Change username
   */
  changeUsername: async (data: ChangeUsernameDto): Promise<void> => {
    await apiClient.patch('/users/username', data);
  },

  /**
   * OAuth Google login - redirect to Google authorization
   */
  loginWithGoogle: (): void => {
    const { CLIENT_ID, REDIRECT_URI } = OAUTH_CONFIG.GOOGLE;
    const scope = 'openid profile email';

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=code&` +
      `access_type=offline&` +
      `prompt=consent`;

    window.location.href = googleAuthUrl;
  },

  /**
   * OAuth Yandex login - redirect to Yandex authorization
   */
  loginWithYandex: (): void => {
    const { CLIENT_ID, REDIRECT_URI } = OAUTH_CONFIG.YANDEX;

    const yandexAuthUrl =
      `https://oauth.yandex.ru/authorize?` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `response_type=code&` +
      `scope=login:info login:email`;

    window.location.href = yandexAuthUrl;
  },

  /**
   * Handle OAuth callback - exchange code for tokens
   */
  handleOAuthCallback: async (provider: 'google' | 'yandex', code: string, redirectUri?: string, referralCode?: string, source?: string, deviceCode?: string): Promise<TokenDto> => {
    const defaultRedirectUri = provider === 'google'
      ? OAUTH_CONFIG.GOOGLE.REDIRECT_URI
      : OAUTH_CONFIG.YANDEX.REDIRECT_URI;
    const callbackUri = redirectUri || defaultRedirectUri;
    const requestData: OAuthCodeRequestDto = {
      code,
      redirectUri: callbackUri,
    };
    if (referralCode) {
      requestData.referralCode = referralCode;
    }
    if (source) {
      requestData.source = source;
    }
    if (deviceCode) {
      requestData.deviceCode = deviceCode;
    }
    const response = await apiClient.post<TokenDto>(`/auth/oauth/${provider}`, requestData);
    return response.data;
  },

  /**
   * OAuth VK ID login - redirect to VK authorization with PKCE
   */
  loginWithVK: async (): Promise<void> => {
    const { CLIENT_ID, REDIRECT_URI } = OAUTH_CONFIG.VK;

    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    // Store PKCE data for callback verification
    storePKCEData(codeVerifier, state);

    const vkAuthUrl =
      `https://id.vk.com/authorize?` +
      `response_type=code&` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `code_challenge=${codeChallenge}&` +
      `code_challenge_method=S256&` +
      `state=${state}&` +
      `scope=email`;

    window.location.href = vkAuthUrl;
  },

  /**
   * Handle VK OAuth callback - exchange code for tokens with PKCE
   */
  handleVKCallback: async (
    code: string,
    codeVerifier: string,
    deviceId: string,
    state: string,
    redirectUri?: string,
    referralCode?: string,
    source?: string,
    deviceCode?: string
  ): Promise<TokenDto> => {
    const callbackUri = redirectUri || OAUTH_CONFIG.VK.REDIRECT_URI;
    const requestData: VkIdCodeRequestDto = {
      code,
      codeVerifier,
      deviceId,
      state,
      redirectUri: callbackUri,
    };
    if (referralCode) {
      requestData.referralCode = referralCode;
    }
    if (source) {
      requestData.source = source;
    }
    if (deviceCode) {
      requestData.deviceCode = deviceCode;
    }
    const response = await apiClient.post<TokenDto>('/auth/oauth/vkid', requestData);
    return response.data;
  },

  /**
   * Confirm device code for authenticated user (from OdiumLauncher)
   */
  confirmDeviceCode: async (code: string): Promise<void> => {
    const osInfo = navigator.userAgent.match(/\(([^)]+)\)/)?.[1] || 'Unknown';
    await apiClient.post(`/auth/device-codes/${code}`, null, {
      headers: { 'X-User-Agent': `OdiumLauncher/1.0 (${osInfo})` },
    });
  },
};
